import React, { useState } from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useQuery, useMutation } from "@apollo/client";
import { useStyles } from "./styles";
import {
  handleChangeRowsPerPage,
  handleCloseSnackbar,
  handleChangePage,
} from "./helpers";
import { UPDATE_EXAM_PAPER_DELIVERY_EXICUTION } from "utils/graphql/services/ai-evaluation/mutations";
import FilterComponent from "shared/molecules/Filter";
import FilterInputs from "./filter/FilterInputs";
import { GET_ALL_EXAM_PAPERS_DELIVERY_EXICUTION } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";

const Alert = React.forwardRef(function Alert(props: any, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function DeliveryExicutionList() {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilter, setIsFilter] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [examPaperDeliveryId, setExamPaperDeliveryId] = useState("");
  const [collegeId, setCollegeId] = useState("");
  const [academicYearId, setAcademicYearId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [classId, setClassId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [isArchieved, setIsArchieved] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTill, setCreatedTill] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTill, setUpdatedTill] = useState("");

  const handleApplyFilter = () => {
    refetch({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      game_search: searchQuery,
      examPaperDeliveryId,
      collegeId,
      academicYearId,
      batchId,
      classId,
      boardId,
      isArchieved,
      createdFrom,
      createdTill,
      updatedFrom,
      updatedTill,
    });
    setIsFilter(false);
  };

  const handleClearFilter = () => {
    setExamPaperDeliveryId("");
    setCollegeId("");
    setAcademicYearId("");
    setBatchId("");
    setClassId("");
    setBoardId("");
    setIsArchieved("");
    setCreatedFrom("");
    setCreatedTill("");
    setUpdatedFrom("");
    setUpdatedTill("");

    refetch({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      game_search: "",
    });
    setIsFilter(false);
  };

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    rowId: number | null;
  }>({ open: false, rowId: null });

  const { data, refetch } = useQuery(GET_ALL_EXAM_PAPERS_DELIVERY_EXICUTION, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      game_search: searchQuery,
    },
    client:autoGradeClient
  });

  const [updateDelivery] = useMutation(UPDATE_EXAM_PAPER_DELIVERY_EXICUTION, {
    onCompleted: () => {
      setSnackbar({
        open: true,
        message: "Delivery archived successfully!",
        severity: "success",
      });
      setConfirmDialog({ open: false, rowId: null });
      refetch();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message || "Error updating delivery.",
        severity: "error",
      });
    },
  });

  const dummyDeliveries = [
    {
      id: 1,
      deliveryId: 5001,
      collegeName: "NCM College",
      academicYearName: "AY 2024-25",
      batchName: "Batch A",
      className: "10th",
      boardName: "CBSE",
      isArchived: false,
      createdByName: "Raj Tupe",
      updatedByName: "Raj Tupe",
      createdAt: "2025-01-10 10:00",
      updatedAt: "2025-01-12 12:30",
    },
    {
      id: 2,
      deliveryId: 5002,
      collegeName: "City Jr College",
      academicYearName: "AY 2024-25",
      batchName: "Batch B",
      className: "12th",
      boardName: "State Board",
      isArchived: true,
      createdByName: "Raj Tupe",
      updatedByName: "Raj Tupe",
      createdAt: "2025-02-01 09:15",
      updatedAt: "2025-02-03 14:45",
    },
  ];

  const deliveries =
    (data?.GetGames?.games && data.GetGames.games.length > 0
      ? data.GetGames.games
      : dummyDeliveries) || [];

  const totalCount = data?.GetGames?.totalCount ?? deliveries.length;

  const handleConfirmArchive = () => {
    if (!confirmDialog.rowId) return;
    updateDelivery({
      variables: { id: confirmDialog.rowId, isArchived: true },
    });
  };

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Paper elevation={3} className={classes.paper}>
          <Grid item mt={2}>
            <Box display="flex" justifyContent="flex-end">
              <FilterComponent isFilter={isFilter} setIsFilter={setIsFilter}>
                <div>
                  <FilterInputs
                    examPaperDeliveryId={examPaperDeliveryId}
                    setExamPaperDeliveryId={setExamPaperDeliveryId}
                    collegeId={collegeId}
                    setCollegeId={setCollegeId}
                    academicYearId={academicYearId}
                    setAcademicYearId={setAcademicYearId}
                    batchId={batchId}
                    setBatchId={setBatchId}
                    classId={classId}
                    setClassId={setClassId}
                    boardId={boardId}
                    setBoardId={setBoardId}
                    isArchieved={isArchieved}
                    setIsArchieved={setIsArchieved}
                    createdFrom={createdFrom}
                    setCreatedFrom={setCreatedFrom}
                    createdTill={createdTill}
                    setCreatedTill={setCreatedTill}
                    updatedFrom={updatedFrom}
                    setUpdatedFrom={setUpdatedFrom}
                    updatedTill={updatedTill}
                    setUpdatedTill={setUpdatedTill}
                    handleApplyFilter={handleApplyFilter}
                    handleClearFilter={handleClearFilter}
                  />
                </div>
              </FilterComponent>
            </Box>
          </Grid>
        </Paper>
        <TableContainer
          component={Paper}
          className={classes.tableContainer}
          sx={{ maxHeight: 500, overflowX: "auto" }}
        >
          <Table stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Delivery Id</TableCell>
                <TableCell>College Name</TableCell>
                <TableCell>Academic Year</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Board</TableCell>
                <TableCell>Is Archived</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Updated By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.deliveryId}</TableCell>
                  <TableCell>{row.collegeName}</TableCell>
                  <TableCell>{row.academicYearName}</TableCell>
                  <TableCell>{row.batchName}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.boardName}</TableCell>
                  <TableCell>{row.isArchived ? "Yes" : "No"}</TableCell>
                  <TableCell>{row.createdByName}</TableCell>
                  <TableCell>{row.updatedByName}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                  <TableCell>{row.updatedAt}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={row.isArchived}
                      onClick={() =>
                        setConfirmDialog({ open: true, rowId: row.id })
                      }
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.paginationWrapper}>
          <TablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => handleChangePage(e, newPage, setPage)}
            onRowsPerPageChange={(e: any) =>
              handleChangeRowsPerPage(e, setRowsPerPage, setPage)
            }
            rowsPerPageOptions={[]}
            component="div"
          />
        </div>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, rowId: null })}
      >
        <DialogTitle>Confirm Archive</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to archive this item?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, rowId: null })}
            color="secondary"
          >
            No
          </Button>
          <Button onClick={handleConfirmArchive} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
      >
        <Alert
          onClose={() => handleCloseSnackbar(setSnackbar, snackbar)}
          severity={
            snackbar.severity as
              | "error"
              | "info"
              | "success"
              | "warning"
              | undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default DeliveryExicutionList;
